import { CustomTable } from '../../../components/Table';
import i18n, {
  DateFormatOptionsForFileNames,
  formatNumber,
} from '@/utils/i18n';
import { DEFAULT_CONTAINER_TYPE_ICON } from '@/utils/imageUrl';
import { Button } from 'reactstrap';
import ClassicLoadingSpinner from '../../../components/LoadingSpinner/ClassicLoadingSpinner';
import { FaDownload } from 'react-icons/fa';
import { GetItemLocationStockQuery } from '../../../graphql/queries/getItemLocationStock.generated';

import { toCsvBlob } from '@/utils/data';
import { download, sanitizeFilename } from '@/utils/download';
import ContainerTypeDisplay from '../../../components/ContainerType/ContainerTypeDisplay';
import TrackableItemDisplay from '../../../components/TrackableItem/TrackableItemDisplay';
import { TrackableItem } from '../../../graphql/generatedTypes';

type ItemLocationStockViewProps = {
  stock: GetItemLocationStockQuery | undefined;
  loadingStock: boolean;
  stockError: any;
  itemLocationName: string;
};

export function ItemLocationStockView({
  stock,
  loadingStock,
  stockError,
  itemLocationName,
}: ItemLocationStockViewProps) {
  function downloadItemLocationStock() {
    if (!stock?.getItemLocationStock) {
      return;
    }
    const csvData = [
      ...stock.getItemLocationStock.container_station_amounts.map((item) => ({
        item_name: item.item_type.name,
        container_type_id: '',
        item_type: 'Container Station',
        amount_dirty: item.amount,
        amount_clean: item.amount_sealed,
        counted: item.counted,
        countLater: item.countLater,
        sealed: item.amount_sealed,
        empty: item.empty,
        sum: item.amount + item.amount_sealed,
      })),
      ...stock.getItemLocationStock.container_type_amounts.map((item) => ({
        item_name: item.container_type_name,
        container_type_id: item.container_type_id,
        item_type: 'Container Type',
        amount_dirty: item.amount,
        amount_clean: item.amount_sealed,
        counted: '-',
        countLater: '-',
        sealed: '-',
        empty: '-',
        sum: item.amount + item.amount_sealed,
      })),
      ...stock.getItemLocationStock.asset_amounts.map((item) => ({
        item_name: item.item_type.name,
        container_type_id: '',
        item_type: 'Asset',
        amount_dirty: '-',
        amount_clean: '-',
        counted: '-',
        countLater: '-',
        sealed: '-',
        empty: '-',
        sum: item.amount,
      })),
    ];

    const csvBlob = toCsvBlob(csvData);

    const date = new Date()
      .toLocaleDateString(i18n.language, DateFormatOptionsForFileNames)
      .replaceAll('/', '-');

    const filename = `stock_item_location_in_${sanitizeFilename(
      itemLocationName,
    )}_${date}.csv`;

    download(csvBlob, filename);
  }

  const {
    sum_amount,
    sum_amount_sealed,
    sum_counted,
    sum_countLater,
    sum_sealed,
    sum_empty,
    sum_assets,
    sum_sum,
  } = [
    ...(stock?.getItemLocationStock.container_station_amounts || []),
    ...(stock?.getItemLocationStock.container_type_amounts || []),
  ].reduce(
    (acc, cur) => {
      if (!cur) {
        return acc;
      }

      const amount = Number(cur?.amount) || 0;
      const amount_sealed = Number(cur?.amount_sealed) || 0;
      // @ts-ignore
      const counted = Number(cur?.counted) || 0;
      // @ts-ignore
      const countLater = Number(cur?.countLater) || 0;
      // @ts-ignore
      const empty = Number(cur?.empty) || 0;
      // @ts-ignore
      const isContainerStation = cur?.type !== undefined;

      if (isContainerStation) {
        if (amount_sealed) {
          // if containerStation then only count sealed
          acc.sum_sealed += amount_sealed;
        }
      } else {
        if (amount) {
          acc.sum_amount += amount;
        }

        if (amount_sealed) {
          acc.sum_amount_sealed += amount_sealed;
        }
      }

      if (counted) {
        acc.sum_counted += counted;
      }

      if (countLater) {
        acc.sum_countLater += countLater;
      }

      if (empty) {
        acc.sum_empty += empty;
      }

      acc.sum_sum = acc.sum_sum + amount + amount_sealed;

      return acc;
    },
    {
      sum_amount: 0,
      sum_amount_sealed: 0,
      sum_counted: 0,
      sum_countLater: 0,
      sum_sealed: 0,
      sum_empty: 0,
      sum_assets: 0,
      sum_sum: 0,
    },
  );

  // Calculate asset totals separately
  const assetTotals = (stock?.getItemLocationStock.asset_amounts || []).reduce(
    (acc, asset) => {
      const amount = Number(asset?.amount) || 0;
      acc.sum_assets += amount;
      acc.sum_sum += amount;
      return acc;
    },
    { sum_assets: 0, sum_sum: 0 },
  );

  const sortedContainerStations = [
    ...(stock?.getItemLocationStock.container_station_amounts || []),
  ].sort((a, b) => a.item_type.id - b.item_type.id);

  return (
    <>
      <CustomTable
        striped
        hover
        bordered
        loading={loadingStock}
        error={stockError}
        headers={[
          i18n.t('ItemLocation:itemType'),
          i18n.t('Common:amountDirty'),
          i18n.t('Common:amountClean'),
          i18n.t('Common:empty'),
          i18n.t('Common:counted'),
          i18n.t('Common:countLater'),
          i18n.t('Common:sealed'),
          i18n.t('ItemLocation:sum'),
        ]}
        rows={[
          sortedContainerStations?.map((stock) => {
            const amount_sealed = Number(stock.amount_sealed) || 0;
            const empty = Number(stock.empty) || 0;
            const counted = Number(stock.counted) || 0;
            const countLater = Number(stock.countLater) || 0;

            return (
              <tr key={stock.item_type.id}>
                <td>
                  <TrackableItemDisplay
                    trackableItem={{
                      id: stock.item_type.id.toString(),
                      name: stock.item_type.name,
                      item_type: stock.item_type,
                    }}
                    showTrackableItemId={false}
                    showLink={false}
                    size={'s'}
                  />
                </td>
                <td className="numbers-formatted">-</td>
                <td className="numbers-formatted">-</td>
                <td className="numbers-formatted">{formatNumber(empty)}</td>
                <td className="numbers-formatted">{formatNumber(counted)}</td>
                <td className="numbers-formatted">
                  {formatNumber(countLater)}
                </td>
                <td className="numbers-formatted">
                  {formatNumber(amount_sealed)}
                </td>
                <td className="numbers-formatted">
                  {formatNumber(amount_sealed + empty + counted + countLater)}
                </td>
              </tr>
            );
          }),
          stock?.getItemLocationStock.container_type_amounts.map((stock) => {
            const amount = Number(stock.amount) || 0;
            const amount_sealed = Number(stock.amount_sealed) || 0;

            return (
              <tr key={stock.container_type_id}>
                <td>
                  <ContainerTypeDisplay
                    containerType={{
                      id: 0,
                      imageUrl:
                        stock.container_type_image ||
                        DEFAULT_CONTAINER_TYPE_ICON,
                      name: stock.container_type_name,
                      hexCode: '',
                    }}
                    showContainerId={false}
                    size="s"
                    showName
                  />
                </td>
                <td className="numbers-formatted">{formatNumber(amount)}</td>
                <td className="numbers-formatted">
                  {formatNumber(amount_sealed)}
                </td>
                <td className="numbers-formatted">-</td>
                <td className="numbers-formatted">-</td>
                <td className="numbers-formatted">-</td>
                <td className="numbers-formatted">-</td>
                <td className="numbers-formatted">
                  {formatNumber(stock.amount + stock.amount_sealed)}
                </td>
              </tr>
            );
          }),
          stock?.getItemLocationStock.asset_amounts.map((asset) => {
            const amount = Number(asset.amount) || 0;

            return (
              <tr key={`asset-${asset.item_type.id}`}>
                <td>
                  <TrackableItemDisplay
                    trackableItem={
                      {
                        item_type: asset.item_type,
                      } as TrackableItem
                    }
                    showTypeInsteadOfName={true}
                    showTrackableItemId={false}
                    showLink={false}
                    size={'s'}
                  />
                </td>
                <td className="numbers-formatted">-</td>
                <td className="numbers-formatted">-</td>
                <td className="numbers-formatted">-</td>
                <td className="numbers-formatted">-</td>
                <td className="numbers-formatted">-</td>
                <td className="numbers-formatted">-</td>
                <td className="numbers-formatted">{formatNumber(amount)}</td>
              </tr>
            );
          }),
          <tr key="spacer-row">
            <td />
            <td />
            <td />
            <td />
            <td />
            <td />
            <td />
            <td />
          </tr>,
          <tr key="sum-row">
            <td>{i18n.t('Common:sum')}</td>
            <td className="numbers-formatted">{formatNumber(sum_amount)}</td>
            <td className="numbers-formatted">
              {formatNumber(sum_amount_sealed)}
            </td>
            <td className="numbers-formatted">{formatNumber(sum_empty)}</td>
            <td className="numbers-formatted">{formatNumber(sum_counted)}</td>
            <td className="numbers-formatted">
              {formatNumber(sum_countLater)}
            </td>
            <td className="numbers-formatted">{formatNumber(sum_sealed)}</td>
            <td className="numbers-formatted">
              {formatNumber(sum_sum + assetTotals.sum_sum)}
            </td>
          </tr>,
        ]
          .filter((arr) => arr != null)
          .flatMap((arr) => arr)}
      />

      <Button
        className="float-end ms-2 mt-2 mb-2"
        onClick={() => downloadItemLocationStock()}
        disabled={!stock?.getItemLocationStock}
      >
        {!stock?.getItemLocationStock ? (
          <ClassicLoadingSpinner />
        ) : (
          <FaDownload />
        )}
        {' ' + i18n.t('Common:download')}
      </Button>
    </>
  );
}
